import React, { useRef, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import ButtonGroup from '@mui/material/ButtonGroup'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import { isArray } from 'reusable/utils/array-utils'
import ButtonWithProgress from '../progress-button/ButtonWithProgress'
import useStore from 'reusable/mobx-store-utils'
import { themeConfig } from '@gap/shared/theme'

export interface SplitButtonAction {
   id?: string
   title: string
   callback: (event: React.MouseEvent<HTMLElement, MouseEvent>) => any
   hasPriority?: boolean
   disabled?: boolean
   /** This only works for single buttons for now */
   tooltip?: string
   loading?: boolean
}

export interface SplitButtonProps extends Omit<React.ComponentProps<typeof ButtonGroup>, 'children'> {
   options: SplitButtonAction[]
}

export const SplitButton = observer(function SplitButton
   ({ options: actions, sx, ...props }: SplitButtonProps) {
   const anchorRef = useRef<HTMLDivElement | null>(null)

   const splitButtonStore = useStore(sp => ({
      open: false,
      get sortedActions() {
         return [...sp.actions.filter(a => !!a.hasPriority), ...sp.actions.filter(a => !a.hasPriority)]
      },
      handleToggle: (event: React.MouseEvent<HTMLElement>) => {
         event.stopPropagation()
         splitButtonStore.open = !splitButtonStore.open
      },
      handleClose: (event: React.MouseEvent<HTMLElement>) => {
         event.stopPropagation()
         splitButtonStore.open = false
      },
   }), { actions })

   return <>
      <ButtonGroup
         sx={[
            theme => ({
               '& .MuiButtonGroup-grouped': {
                  '&.MuiButtonGroup-grouped:not(:first-of-type)': {
                     minWidth: themeConfig.spacing * 3.5,
                  },
               },
               ...(!isArray(sx) ? (typeof sx === 'function' ? sx(theme) : sx) : {})
            }),
            ...(isArray(sx) ? sx : []),
         ]}
         {...props}
         ref={anchorRef}
         aria-label="split button">
         <ButtonWithProgress
            disabled={splitButtonStore.sortedActions[0].disabled}
            onClick={splitButtonStore.sortedActions[0].callback}
            sx={{ whiteSpace: 'nowrap' }}
            loading={splitButtonStore.sortedActions[0].loading || false}>
            {splitButtonStore.sortedActions[0].title}
         </ButtonWithProgress>
         {splitButtonStore.sortedActions.length > 1 && (
            <ButtonWithProgress
               color="primary"
               size="small"
               aria-owns={splitButtonStore.open ? 'menu-list-grow' : undefined}
               aria-haspopup="true"
               onClick={splitButtonStore.handleToggle}
               disabled={splitButtonStore.sortedActions[0].loading || false}
               disableRipple
               sx={{
                  minWidth: themeConfig.spacing * 3,
                  width: themeConfig.spacing * 3,
                  padding: 0,
               }}>
               <ArrowDropDownIcon sx={{ mr: '5px' }} fontSize="small" />
            </ButtonWithProgress>
         )}
      </ButtonGroup>
      <Menu open={splitButtonStore.open} anchorEl={anchorRef.current} onClose={splitButtonStore.handleClose}>
         {splitButtonStore.sortedActions.map((option, index) =>
            index > 0 ? (
               <SplitButtonItem key={index} option={option} handleClose={splitButtonStore.handleClose} />
            ) : null
         )}
      </Menu>
   </>
})

interface SplitButtonItemProps {
   option: SplitButtonAction
   handleClose: (event: React.MouseEvent<HTMLElement>) => any
}

const SplitButtonItem = React.forwardRef<HTMLLIElement, SplitButtonItemProps>(
   ({ option, handleClose }, ref) => {
      const { title, callback, disabled } = option

      const handleClick = useCallback(
         (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            handleClose(event)
            callback(event)
         },
         [handleClose, callback]
      )

      return <MenuItem ref={ref} disabled={disabled} onClick={handleClick}>
         {' '}
         {title}{' '}
      </MenuItem>
   }
)
